import React, { useState, useEffect, useRef } from 'react';
import { X, MapPin, Navigation, Crosshair, Loader2, AlertCircle, Check } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const C = {
  accent:     "#6C8EEF",
  accentDark: "#8B7FC7",
  navy:       "#2D2D4E",
  navyLight:  "rgba(108,142,239,0.08)",
  border:     "rgba(255,255,255,0.85)",
  borderSoft: "rgba(108,142,239,0.18)",
  bg:         "rgba(255,255,255,0.60)",
  footerBg:   "rgba(232,236,247,0.80)",
  activePill: "linear-gradient(135deg, #8B7FC7, #6C8EEF)",
  confirmBtn: "linear-gradient(135deg, #8B7FC7, #6C8EEF)",
  shadow:     "0 6px 20px rgba(108,142,239,0.35)",
};

const LocationModal = ({ isOpen, onClose, onSelectLocation }) => {
  const [activeTab, setActiveTab] = useState('map');
  const [mapLoaded, setMapLoaded] = useState(false);
  const { t } = useTranslation('location');
  const [location, setLocation] = useState(null);
  const [address, setAddress] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [radius, setRadius] = useState(2);
  const [confirming, setConfirming] = useState(false);

   const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const deviceLocationDone = useRef(false);

  useEffect(() => {
    if (!isOpen) return;
    const GOOGLE_MAPS_API_KEY = import.meta.env.VITE_GOOGLE_MAPS_API_KEY;
    if (!GOOGLE_MAPS_API_KEY) { setError('Google Maps API key not configured'); return; }

    window.gm_authFailure = () => {
      setError(t('map_billing_error', 'Map unavailable — please try again later'));
      setMapLoaded(false);
    };

    // Already fully loaded — just flip the flag
    if (window.google && window.google.maps && typeof window.google.maps.Map === 'function') {
      setMapLoaded(true);
      return;
    }

    const callbackName = '__googleMapsInitCB';

    // Register/replace the callback every time so a reopen always gets notified
    window[callbackName] = () => {
      setMapLoaded(true);
      delete window[callbackName];
    };

    // Remove any stale Maps script (old `loading=async` tag or untagged remnant)
    // so we inject a fresh one with the correct `callback` param
    document.querySelectorAll('script[src*="maps.googleapis.com/maps/api/js"]').forEach(s => s.remove());

    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_MAPS_API_KEY}&loading=async&callback=${callbackName}&language=bg&libraries=places`;
    script.async = true;
    script.defer = true;
    script.onerror = () => setError('Failed to load Google Maps');
    document.head.appendChild(script);

    return () => {
      if (window[callbackName]) delete window[callbackName];
    };
  }, [isOpen, t]);

  useEffect(() => {
    if (!isOpen || activeTab !== 'map' || !mapLoaded || !mapRef.current) return;
    if (mapInstanceRef.current) return;
    if (!window.google || !window.google.maps || typeof window.google.maps.Map !== 'function') return;
    const defaultCenter = { lat: 42.6977, lng: 23.3219 };
    try {
      const map = new window.google.maps.Map(mapRef.current, {
        center: location || defaultCenter,
        zoom: 15,
        disableDefaultUI: true,
        zoomControl: true,
        gestureHandling: 'greedy',
        styles: [{ featureType: 'poi', elementType: 'labels', stylers: [{ visibility: 'off' }] }],
      });
      mapInstanceRef.current = map;
      map.addListener('center_changed', () => {
        const center = map.getCenter();
        setLocation({ lat: center.lat(), lng: center.lng() });
      });
      const center = map.getCenter();
      setLocation({ lat: center.lat(), lng: center.lng() });
    } catch (err) {
      console.error(err);
      setError(t('map_error'));
    }
  }, [isOpen, activeTab, mapLoaded, location, t]);

 useEffect(() => {
    if (!isOpen) {
      mapInstanceRef.current = null;
      deviceLocationDone.current = false;
    }
  }, [isOpen]);

  const reverseGeocodeLocation = async (loc) => {    try {
      const response = await fetch(`${import.meta.env.VITE_REACT_APP_API_URL}/api/cities/reverse-geocode`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ latitude: loc.lat, longitude: loc.lng }),
      });
      const data = await response.json();
      if (data.success) setAddress(data.data.formatted_address);
    } catch {
      setError(t('generic_error'));
    }
  };

  const handleUseDeviceLocation = async () => {
    setLoading(true);
    setError('');
    if (!navigator.geolocation) {
      setError(t('unavailable'));
      setLoading(false);
      return;
    }
   navigator.geolocation.getCurrentPosition(
      async (position) => {
        const loc = { lat: position.coords.latitude, lng: position.coords.longitude };
        setLocation(loc);
        await reverseGeocodeLocation(loc);
        deviceLocationDone.current = true;
        setLoading(false);
      },
      (err) => {
        setLoading(false);
        switch (err.code) {
          case err.PERMISSION_DENIED:    setError(t('error_permission_denied'));    break;
          case err.POSITION_UNAVAILABLE: setError(t('error_position_unavailable')); break;
          case err.TIMEOUT:              setError(t('error_timeout'));              break;
          default:                       setError(t('generic_error'));
        }
        setActiveTab('map');
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
    );
  };

  const handleConfirm = async () => {
    if (!location) return;
    setConfirming(true);
    let resolvedAddress = address;
    try {
      const response = await fetch(`${import.meta.env.VITE_REACT_APP_API_URL}/api/cities/reverse-geocode`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ latitude: location.lat, longitude: location.lng }),
      });
      const data = await response.json();
      if (data.success) { resolvedAddress = data.data.formatted_address; setAddress(resolvedAddress); }
    } catch (err) { console.error('Reverse geocode error:', err); }
    setConfirming(false);
    onSelectLocation({ type: 'coordinates', latitude: location.lat, longitude: location.lng, radius, address: resolvedAddress });
    onClose();
  };

  const handleRecenter = () => {
    if (mapInstanceRef.current && location) mapInstanceRef.current.panTo(location);
  };

  if (!isOpen) return null;

  const radiusOptions = [0.5, 1, 2, 5];

  return (
    <div
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4"
      style={{ background: "rgba(45,45,78,0.45)", backdropFilter: "blur(8px)" }}
    >
      <div
        className="w-full sm:max-w-2xl flex flex-col overflow-hidden rounded-t-3xl sm:rounded-3xl"
        style={{
          maxHeight: "95dvh",
          background: "rgba(255,255,255,0.88)",
          backdropFilter: "blur(20px)",
          WebkitBackdropFilter: "blur(20px)",
          boxShadow: "0 24px 80px rgba(45,45,78,0.22)",
          border: "1px solid rgba(255,255,255,0.90)",
        }}
      >
        {/* Drag handle — mobile only */}
        <div className="flex justify-center pt-3 pb-1 sm:hidden flex-shrink-0">
          <div className="w-10 h-1 rounded-full" style={{ background: "#6C8EEF", opacity: 0.4 }} />
        </div>

        {/* Header */}
        <div
          className="flex items-center justify-between px-5 sm:px-6 py-3.5 sm:py-4 flex-shrink-0"
          style={{ borderBottom: "1px solid rgba(108,142,239,0.15)" }}
        >
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-xl flex items-center justify-center" style={{ background: "rgba(108,142,239,0.12)" }}>
              <MapPin className="w-4 h-4" style={{ color: "#6C8EEF" }} />
            </div>
            <h2 className="text-sm sm:text-base font-bold" style={{ color: "#2D2D4E" }}>
              {t('select_location')}
            </h2>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-xl flex items-center justify-center transition-colors"
            style={{ color: "#8A8AAA", background: "transparent" }}
            onMouseEnter={e => e.currentTarget.style.background = "rgba(108,142,239,0.08)"}
            onMouseLeave={e => e.currentTarget.style.background = "transparent"}
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex flex-shrink-0" style={{ borderBottom: "1px solid rgba(108,142,239,0.15)" }}>
          {[
            { id: 'map',    label: t('map_tab'),    icon: MapPin },
            { id: 'device', label: t('device_tab'), icon: Navigation },
          ].map(({ id, label, icon: Icon }) => (
            <button
              key={id}
             onClick={() => {
                if (id !== 'map') mapInstanceRef.current = null;
                setActiveTab(id);
                if (id === 'device' && !deviceLocationDone.current) handleUseDeviceLocation();
              }}
              className="flex-1 flex items-center justify-center gap-1.5 sm:gap-2 py-3 sm:py-3.5 text-xs sm:text-sm font-semibold transition-all"
              style={activeTab === id ? { color: "#6C8EEF", borderBottom: "2px solid #6C8EEF" } : { color: "#8A8AAA" }}
            >
              <Icon className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
              {label}
            </button>
          ))}
        </div>

        {/* Map tab */}
        {activeTab === 'map' && (
          <div className="relative w-full h-[380px] sm:h-[420px] flex-shrink-0">
            <div ref={mapRef} className="w-full h-full" />
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 pointer-events-none z-10">
              <MapPin className="w-9 h-9 sm:w-10 sm:h-10 drop-shadow-lg" style={{ color: "#6C8EEF", strokeWidth: 2.5 }} />
            </div>
            {location && (
              <button
                onClick={handleRecenter}
                className="absolute top-3 right-3 p-2.5 rounded-xl transition"
                style={{
                  background: "rgba(255,255,255,0.80)",
                  backdropFilter: "blur(12px)",
                  border: "1px solid rgba(255,255,255,0.90)",
                  boxShadow: "0 4px 16px rgba(100,100,160,0.10)",
                }}
                title={t("recenter")}
                onMouseEnter={e => e.currentTarget.style.background = "rgba(255,255,255,0.95)"}
                onMouseLeave={e => e.currentTarget.style.background = "rgba(255,255,255,0.80)"}
              >
                <Crosshair className="w-4 h-4 sm:w-5 sm:h-5" style={{ color: "#2D2D4E" }} />
              </button>
            )}
            {!mapLoaded && (
              <div className="absolute inset-0 flex items-center justify-center" style={{ background: "rgba(232,236,247,0.70)" }}>
                <Loader2 className="w-8 h-8 animate-spin" style={{ color: "#6C8EEF" }} />
              </div>
            )}
          </div>
        )}

        {/* Device tab */}
        {activeTab === 'device' && (
          <div className="flex flex-col items-center justify-center overflow-y-auto" style={{ minHeight: 280, padding: "2rem 1.5rem" }}>
            {loading ? (
              <div className="text-center">
                <div className="w-14 h-14 rounded-2xl flex items-center justify-center mx-auto mb-4" style={{ background: "rgba(108,142,239,0.10)" }}>
                  <Loader2 className="w-7 h-7 animate-spin" style={{ color: "#6C8EEF" }} />
                </div>
                <p className="font-medium text-sm" style={{ color: "#2D2D4E" }}>{t('detecting_location')}</p>
              </div>
            ) : error ? (
              <div className="text-center max-w-sm w-full">
                <div className="w-14 h-14 rounded-2xl flex items-center justify-center mx-auto mb-4" style={{ background: "rgba(224,108,176,0.10)" }}>
                  <AlertCircle className="w-7 h-7" style={{ color: "#E06CB0" }} />
                </div>
                <p className="mb-5 leading-relaxed text-sm" style={{ color: "#2D2D4E" }}>{error}</p>
                <button
                  onClick={() => setActiveTab('map')}
                  className="px-5 py-2.5 rounded-xl text-white font-semibold text-sm transition-all duration-200"
                  style={{ background: C.confirmBtn, boxShadow: C.shadow }}
                >
                  {t('use_map_instead')}
                </button>
              </div>
            ) : location ? (
              <div className="text-center max-w-sm w-full">
                <div className="w-14 h-14 rounded-2xl flex items-center justify-center mx-auto mb-4" style={{ background: "rgba(89,195,232,0.12)" }}>
                  <Check className="w-7 h-7" style={{ color: "#59C3E8" }} />
                </div>
                <h3 className="text-sm sm:text-base font-bold mb-1" style={{ color: "#2D2D4E" }}>{t('location_found')}</h3>
                <p className="text-xs sm:text-sm mb-6 px-2" style={{ color: "#8B7FC7" }}>
                  {address || t('loading_address')}
                </p>
                <p className="text-[10px] font-bold uppercase tracking-widest mb-3" style={{ color: "#6C8EEF" }}>
                  {t('search_radius')}
                </p>
                <div className="flex items-center justify-center gap-2 flex-wrap">
                  {radiusOptions.map((r) => (
                    <button
                      key={r}
                      onClick={() => setRadius(r)}
                      className="px-3.5 py-2 rounded-xl text-sm font-semibold transition-all duration-150"
                      style={
                        radius === r
                          ? { background: C.activePill, color: "#fff", boxShadow: C.shadow }
                          : { background: "rgba(108,142,239,0.08)", color: "#2D2D4E", border: "1px solid rgba(108,142,239,0.20)" }
                      }
                    >
                      {r} {t('km_unit')}
                    </button>
                  ))}
                </div>
              </div>
            ) : null}
          </div>
        )}

        {/* Footer */}
        <div
          className="px-5 sm:px-6 py-4 flex-shrink-0"
          style={{ background: "rgba(255,255,255,0.72)", backdropFilter: "blur(12px)", WebkitBackdropFilter: "blur(12px)", borderTop: "1px solid rgba(108,142,239,0.15)" }}
        >
          <div className="flex items-center justify-between gap-3">
            <div className="flex-1 min-w-0">
              {activeTab === 'map' && (
                <div>
                  <p className="text-[10px] font-bold uppercase tracking-widest mb-2" style={{ color: "#6C8EEF" }}>
                    {t('radius')}
                  </p>
                  <div className="flex items-center gap-1.5 flex-wrap">
                    {radiusOptions.map((r) => (
                      <button
                        key={r}
                        onClick={() => setRadius(r)}
                        className="px-2.5 sm:px-3 py-1.5 rounded-xl text-xs font-semibold transition-all duration-150"
                        style={
                          radius === r
                            ? { background: C.activePill, color: "#fff", boxShadow: C.shadow }
                            : { background: "rgba(255,255,255,0.75)", color: "#2D2D4E", border: "1px solid rgba(108,142,239,0.20)" }
                        }
                      >
                        {r} {t('km_unit')}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className="flex gap-2 flex-shrink-0">
              <button
                onClick={onClose}
                className="px-3.5 sm:px-4 py-2.5 rounded-xl text-xs sm:text-sm font-semibold transition"
                style={{ color: "#5A5A7A", background: "rgba(255,255,255,0.75)", border: "1px solid rgba(108,142,239,0.20)" }}
                onMouseEnter={e => e.currentTarget.style.background = "rgba(255,255,255,0.95)"}
                onMouseLeave={e => e.currentTarget.style.background = "rgba(255,255,255,0.75)"}
              >
                {t('cancel', { ns: 'common' })}
              </button>
              <button
                onClick={handleConfirm}
                disabled={!location || confirming}
                className="px-4 sm:px-6 py-2.5 rounded-xl text-xs sm:text-sm font-bold text-white transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1.5 sm:gap-2"
                style={{ background: C.confirmBtn, boxShadow: C.shadow }}
              >
                {confirming ? (
                  <>
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    <span className="hidden sm:inline">{t('confirming')}</span>
                    <span className="sm:hidden">...</span>
                  </>
                ) : (
                  <>
                    <Check className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                    {t('confirm')}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LocationModal;